export interface Time {
  hours: number
  minutes: number
}

type RoundingStrategy = 'later' | 'sooner'
export interface CreationOptions {
  minutesRounding: RoundingStrategy
}
const DEFAULT_OPTIONS: CreationOptions = { minutesRounding: 'later' }

export function createDate(): PrebookingDate
export function createDate(dateStr?: string, strategy?: CreationOptions): PrebookingDate
export function createDate(date?: Date, strategy?: CreationOptions): PrebookingDate
export function createDate(dateStr?: string | Date, strategy: CreationOptions = DEFAULT_OPTIONS): PrebookingDate {
  const result = new PrebookingDate(strategy.minutesRounding)
  if (dateStr) {
    const date = new Date(dateStr)
    result.date = date
    result.time = {
      hours: date.getHours(),
      minutes: date.getMinutes(),
    }
  }
  return result
}

/**
 * Specific date class that follows the booking date constraints:
 *   - minutes can only be 0 or 30
 *   - date (year, month, day) and time (hour, minute, second) can be modified separately thanks to getter/setter methods
 */
export class PrebookingDate {
  private _date?: Date
  private readonly minutesRoundingStrategy: RoundingStrategy

  constructor(minutesRoundingStrategy: RoundingStrategy) {
    this.minutesRoundingStrategy = minutesRoundingStrategy
  }

  get date(): Date | undefined {
    if (!this._date)
      return undefined

    return new Date(this._date.getTime())
  }

  /**
   * Modify year, month, and/or day without modifying the hour, minute, or seconds
   * @param date
   */
  set date(date: Date) {
    if (date.getTime()) {
      this._date = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        this._date?.getHours() ?? 0,
        this._date?.getMinutes() ?? 0,
        0,
      )
    }
  }

  /**
   * Modify everything (year, month, day, hours, minutes)
   * @param date
   */
  set dateTime(date: Date) {
    this._date = createDate(date).date
  }

  get dateTime(): Date | undefined {
    return this.date
  }

  get time(): Time | undefined {
    return this._date
      ? { hours: this._date.getHours(), minutes: this._date.getMinutes() }
      : undefined
  }

  set time(time: Time) {
    if (this._date) {
      const newDate = new Date(this._date)
      // We cannot simply call the function on _date as normal reactivy want work
      // one will need to have deep watchers if the time is modified using this setter
      newDate.setHours(time.hours, time.minutes, 0, 0)
      roundTime(newDate, this.minutesRoundingStrategy)
      this._date = newDate
    }
  }

  toString(): string | undefined {
    return this._date?.toString()
  }
}

/**
 * Round the minutes to 0 or 30 depnding on the strategy
 * @param date
 * @param roundingStrategy
 *   - later: we will round to the upper value (can increase the hour also). Examples:
 *      - 16:00 -> 16:00
 *      - 16:05 -> 16:30
 *      - 16:30 -> 16:30
 *      - 16:38 -> 17:00
 *   - sooner: we will round to the lower value (can increase the hour also). Examples:
 *      - 16:00 -> 16:00
 *      - 16:05 -> 16:00
 *      - 16:30 -> 16:30
 *      - 16:38 -> 16:30
 */
function roundTime(date: Date, roundingStrategy: RoundingStrategy) {
  const minutes = date.getMinutes()
  if (minutes === 0 || minutes === 30)
    return

  switch (roundingStrategy) {
    case 'later':
      if (minutes < 30)
        date.setMinutes(30)
      else
        date.setMinutes(60) // 60 so that it also increase the hour

      break
    case 'sooner':
      if (date.getMinutes() < 30)
        date.setMinutes(0)
      else
        date.setMinutes(30)

      break
    default: assertUnreachable(roundingStrategy)
  }
}

/**
 * Trick function to detect if the switch case is exhaustive
 *
 * @param arg
 */
function assertUnreachable(arg: never): never {
  throw new Error(`Did not expect to get here with ${arg}`)
}
